import { useCallback, useEffect, useState } from 'react';

import { type AdsSyncStatus, ApiError, fetchAdsSyncStatus, refreshAds } from './api.ts';
import { describeError } from './data.tsx';
import { Icon } from './Icon.tsx';
import { useToast } from './toast.tsx';

/**
 * **Refresh** (meta-ads-agent US-006): fetches the agent's objects and
 * insights from Meta now. The server allows it once per 10 minutes, a run's
 * own sync included, so after a sync the button is disabled and its tooltip
 * says when it can run again. It re-enables on its own when that time comes.
 *
 * Lives in the Ads page header (US-015); `onRefreshed` reloads the page.
 */
export function RefreshAdsButton({
  disabled = false,
  onRefreshed,
}: {
  readonly disabled?: boolean;
  readonly onRefreshed?: () => void;
}) {
  const toast = useToast();
  const [status, setStatus] = useState<AdsSyncStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    fetchAdsSyncStatus(controller.signal)
      .then(setStatus)
      .catch(() => {
        // The button stays usable; the server still enforces the interval.
      });
    return () => {
      controller.abort();
    };
  }, []);

  // Re-render when the wait is over, so the button enables itself.
  const waitMs = status?.nextAllowedAt == null ? 0 : Date.parse(status.nextAllowedAt) - Date.now();
  useEffect(() => {
    if (waitMs <= 0) return;
    const timer = setTimeout(() => {
      setTick((tick) => tick + 1);
    }, waitMs + 250);
    return () => {
      clearTimeout(timer);
    };
  }, [waitMs]);

  const refresh = useCallback((): void => {
    setBusy(true);
    refreshAds()
      .then((outcome) => {
        setStatus(outcome);
        const changes = outcome.result.externalChanges.length;
        toast.ok(
          changes === 0
            ? 'Ads refreshed from Meta.'
            : `Ads refreshed from Meta. ${String(changes)} change${changes === 1 ? '' : 's'} made outside marketingchief.`,
        );
        onRefreshed?.();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof ApiError ? cause.message : describeError(cause));
        // 429/409/502 carry the interval too; read it back for the tooltip.
        fetchAdsSyncStatus().then(setStatus).catch(() => undefined);
      })
      .finally(() => {
        setBusy(false);
      });
  }, [onRefreshed, toast]);

  const waiting = waitMs > 0;
  const title = waiting
    ? `Refreshed ${relative(status?.lastSyncAt ?? null)}. Meta can be asked again at ${clock(status?.nextAllowedAt ?? null)} (at most once per 10 minutes).`
    : status?.lastSyncAt == null
      ? 'Fetch statuses and the last 30 days of numbers from Meta'
      : `Last refreshed ${relative(status.lastSyncAt)}. Fetch statuses and the last 30 days of numbers from Meta`;

  return (
    <button
      type="button"
      className="button"
      onClick={refresh}
      disabled={disabled || busy || waiting || status?.running === true}
      title={title}
    >
      <Icon name="sync" />
      {busy ? 'Refreshing…' : 'Refresh'}
    </button>
  );
}

function clock(iso: string | null): string {
  if (iso === null) return 'soon';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function relative(iso: string | null): string {
  if (iso === null) return 'never';
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  return minutes === 0 ? 'just now' : `${String(minutes)} minute${minutes === 1 ? '' : 's'} ago`;
}
